// The three small things both public pages need to know about a container,
// written once rather than twice. Pure — no React, no network — for the same
// reason panelModel.ts is: `frontend/vitest.config.ts` collects only
// `src/**/*.test.ts`, so anything a `.tsx` page decides is untestable here,
// and anything it merely renders is not.
import type { Container } from '../types'

/**
 * The version index a `/video/:key/v/:index` URL asked for, or undefined when
 * the route carried none.
 *
 * Returns undefined for a malformed index too — the caller distinguishes
 * "no index in the URL" from "an index that is not a version" by checking
 * whether the raw param was present at all, and 404s the second rather than
 * quietly serving the latest version at an address that named another one.
 */
export function requestedVersionIndex(raw: string | undefined): number | undefined {
  if (raw === undefined || raw === '') return undefined
  if (!/^\d+$/.test(raw)) return undefined
  return Number(raw)
}

/**
 * The index of the version a bare container link serves: the last one whose
 * bytes still exist. Null for a container with nothing playable left.
 *
 * A deleted version whose entry has a recorded VersionRef on chain keeps its
 * entry and its index (removing it would renumber the list and break the
 * correspondence with the chain), so this scans backwards rather than taking
 * the last element. An unclaimed deleted version is removed and the list
 * renumbered by the store itself instead (api/pkg/data's planVersionDeletion)
 * — true for every unclaimed entry of an unanchored container, and for the
 * unclaimed, unsealed tail of an anchored one — so the scan finds the same
 * answer either way; it never has to know which case it is looking at.
 */
export function latestVersionIndex(container: Container): number | null {
  for (let i = container.versions.length - 1; i >= 0; i--) {
    if (!container.versions[i].deleted_at) return container.versions[i].index
  }
  return null
}

/** Versions whose bytes the publisher deleted. The chain has no concept of
 * deletion, so the verification panel can only learn this from here. */
export function deletedVideoIds(container: Container): string[] {
  return container.versions.filter((v) => !!v.deleted_at).map((v) => v.video_id)
}
